import React from 'react';
import {
  OwnerShare,
  ShareAccess,
  ShareLayout,
  deleteOwnerShare,
  getOwnerShare,
  putOwnerShare,
  rotateOwnerShare,
  sharePageUrl,
} from './chat/shareService';

interface ShareLinkModalProps {
  open: boolean;
  onClose: () => void;
  conversationUuid: string;
  theme: 'light' | 'dark';
  c: any;
  onChanged?: (active: boolean) => void;
}

const LAYOUT_OPTIONS: { id: ShareLayout; label: string; hint: string }[] = [
  { id: 'brief', label: 'Ringkasan', hint: 'Pembaca melihat memo yang rapi, bukan tanya-jawab.' },
  { id: 'transcript', label: 'Percakapan', hint: 'Tanya-jawab apa adanya, seperti di chat.' },
];

const ACCESS_OPTIONS: { id: ShareAccess; label: string; hint: string }[] = [
  { id: 'public', label: 'Publik', hint: 'Siapa saja yang punya tautan bisa membaca.' },
  { id: 'password', label: 'Kata sandi', hint: 'Pembaca harus mengisi kata sandi. Minimal 12 karakter.' },
  { id: 'email', label: 'Email tertentu', hint: 'Hanya email ini yang bisa buka, lewat kode OTP.' },
];

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  open, onClose, conversationUuid, theme, c, onChanged,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [share, setShare] = React.useState<OwnerShare>({ active: false });
  const [access, setAccess] = React.useState<ShareAccess>('public');
  const [layout, setLayout] = React.useState<ShareLayout>('brief');
  const [showTranscript, setShowTranscript] = React.useState(false);
  const [refreshBrief, setRefreshBrief] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [emailInput, setEmailInput] = React.useState('');
  const [emails, setEmails] = React.useState<string[]>([]);
  const [copied, setCopied] = React.useState(false);
  const [confirmRevoke, setConfirmRevoke] = React.useState(false);
  const [confirmRotate, setConfirmRotate] = React.useState(false);

  const applyShare = (next: OwnerShare) => {
    setShare(next);
    if (next.active && next.access) setAccess(next.access);
    else if (!next.active) setAccess('public');
    setLayout(next.active && next.layout === 'transcript' ? 'transcript' : 'brief');
    setShowTranscript(!!next.show_transcript);
    setRefreshBrief(false);
    setEmails(next.emails || []);
    setPassword('');
    onChanged?.(!!next.active);
  };

  const writingBrief = layout === 'brief' && (!share.active || !share.has_brief || refreshBrief);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setConfirmRevoke(false);
      setConfirmRotate(false);
      setCopied(false);
      try {
        const data = await getOwnerShare(conversationUuid);
        if (!cancelled) applyShare(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Gagal memuat tautan.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, conversationUuid]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !(saving && writingBrief)) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving, writingBrief]);

  const addEmail = () => {
    const value = emailInput.trim().toLowerCase();
    if (!value) return;
    if (!emails.includes(value)) setEmails([...emails, value]);
    setEmailInput('');
  };

  const sameEmails = (a: string[], b: string[]) =>
    a.length === b.length && a.every((item, i) => item === b[i]);

  const pendingEmails = (() => {
    const extra = emailInput.trim().toLowerCase();
    if (!extra || emails.includes(extra)) return emails;
    return [...emails, extra];
  })();

  const dirty = share.active
    ? access !== (share.access || 'public')
      || layout !== (share.layout || 'brief')
      || (layout === 'brief' && showTranscript !== !!share.show_transcript)
      || refreshBrief
      || (access === 'password' && password.length > 0)
      || (access === 'email' && !sameEmails(pendingEmails, share.emails || []))
    : false;

  const canCreate = access === 'public'
    || (access === 'password' && (password.length >= 12 || (!!share.has_password && !password)))
    || (access === 'email' && pendingEmails.length > 0);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const nextEmails = pendingEmails;
      if (emailInput.trim()) {
        setEmails(nextEmails);
        setEmailInput('');
      }
      const body: {
        access: ShareAccess;
        password?: string;
        emails?: string[];
        layout: ShareLayout;
        refresh_brief?: boolean;
        show_transcript?: boolean;
      } = { access, layout, show_transcript: layout === 'brief' && showTranscript };
      if (access === 'password' && password) body.password = password;
      if (access === 'email') body.emails = nextEmails;
      if (layout === 'brief' && refreshBrief) body.refresh_brief = true;
      applyShare(await putOwnerShare(conversationUuid, body));
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan tautan.');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    setSaving(true);
    setError(null);
    try {
      applyShare(await deleteOwnerShare(conversationUuid));
      setConfirmRevoke(false);
    } catch (err: any) {
      setError(err?.message || 'Gagal menutup tautan.');
    } finally {
      setSaving(false);
    }
  };

  const rotate = async () => {
    setSaving(true);
    setError(null);
    try {
      applyShare(await rotateOwnerShare(conversationUuid));
      setConfirmRotate(false);
    } catch (err: any) {
      setError(err?.message || 'Gagal membuat tautan baru.');
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!share.token) return;
    try {
      await navigator.clipboard.writeText(sharePageUrl(share.token));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('Tidak bisa menyalin. Salin tautan secara manual.');
    }
  };

  if (!open) return null;

  const link = share.active && share.token ? sharePageUrl(share.token) : '';
  const panelBg = theme === 'dark' ? '#141619' : '#FFFFFF';

  return (
    <div
      onClick={saving && writingBrief ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Bagikan percakapan"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%', maxWidth: '480px', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column',
          background: panelBg,
          border: `1px solid ${c.line2}`,
          borderRadius: '14px',
          boxShadow: c.shadow,
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 12px', borderBottom: `1px solid ${c.line}`,
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: c.ink }}>Bagikan percakapan</div>
            <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '2px' }}>
              Orang lain membaca lewat tautan. Mereka tidak masuk ke akunmu. File di panel kanan tidak ikut.
            </div>
          </div>
          <button
            onClick={saving && writingBrief ? undefined : onClose}
            disabled={saving && writingBrief}
            aria-label="Tutup"
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              color: c.ink3, fontSize: '18px', lineHeight: 1, padding: '4px 6px',
            }}
          >×</button>
        </div>

        <div style={{ padding: '14px 16px 16px', overflow: 'auto' }}>
          {loading ? (
            <div style={{ fontSize: '12.5px', color: c.ink3 }}>Memuat…</div>
          ) : (
            <>
              <div style={{ fontSize: '11px', fontWeight: 600, color: c.ink3, marginBottom: '8px', letterSpacing: '0.04em' }}>
                BENTUK
              </div>
              {LAYOUT_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  style={{
                    display: 'flex', gap: '10px', alignItems: 'flex-start',
                    padding: '9px 10px', marginBottom: '6px',
                    borderRadius: '10px',
                    border: `1px solid ${layout === opt.id ? c.jade : c.line}`,
                    background: layout === opt.id ? c.jadeDim : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="share-layout"
                    checked={layout === opt.id}
                    onChange={() => setLayout(opt.id)}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: c.ink }}>
                      {opt.label}
                    </span>
                    <span style={{ display: 'block', fontSize: '11.5px', color: c.ink3, marginTop: '2px' }}>
                      {opt.hint}
                    </span>
                  </span>
                </label>
              ))}
              {layout === 'brief' && (
                <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', margin: '4px 0 8px', fontSize: '12px', color: c.ink2, lineHeight: 1.45 }}>
                  <input
                    type="checkbox"
                    checked={showTranscript}
                    onChange={(e) => setShowTranscript(e.target.checked)}
                    style={{ marginTop: '2px' }}
                  />
                  Pembaca boleh lihat percakapan lengkap
                </label>
              )}
              {layout === 'brief' && share.has_brief && (
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '0 0 14px', fontSize: '12px', color: c.ink2 }}>
                  <input
                    type="checkbox"
                    checked={refreshBrief}
                    onChange={(e) => setRefreshBrief(e.target.checked)}
                  />
                  Susun ulang ringkasan dari chat terbaru
                </label>
              )}

              <div style={{ fontSize: '11px', fontWeight: 600, color: c.ink3, margin: '10px 0 8px', letterSpacing: '0.04em' }}>
                SIAPA YANG BISA BUKA
              </div>
              {ACCESS_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  style={{
                    display: 'flex', gap: '10px', alignItems: 'flex-start',
                    padding: '9px 10px', marginBottom: '6px',
                    borderRadius: '10px',
                    border: `1px solid ${access === opt.id ? c.jade : c.line}`,
                    background: access === opt.id ? c.jadeDim : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="share-access"
                    checked={access === opt.id}
                    onChange={() => setAccess(opt.id)}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: c.ink }}>
                      {opt.label}
                    </span>
                    <span style={{ display: 'block', fontSize: '11.5px', color: c.ink3, marginTop: '2px' }}>
                      {opt.hint}
                    </span>
                  </span>
                </label>
              ))}

              {access === 'password' && (
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={share.has_password ? 'Ganti kata sandi (opsional)' : 'Kata sandi, minimal 12 karakter'}
                  autoComplete="new-password"
                  style={{
                    width: '100%', marginTop: '8px', padding: '9px 11px',
                    borderRadius: '8px', border: `1px solid ${c.line2}`,
                    background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff',
                    color: c.ink, fontSize: '13px', fontFamily: 'inherit',
                  }}
                />
              )}

              {access === 'email' && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addEmail();
                        }
                      }}
                      placeholder="nama@perusahaan.com"
                      style={{
                        flex: 1, padding: '9px 11px',
                        borderRadius: '8px', border: `1px solid ${c.line2}`,
                        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff',
                        color: c.ink, fontSize: '13px', fontFamily: 'inherit',
                      }}
                    />
                    <button
                      type="button"
                      onClick={addEmail}
                      style={{
                        padding: '0 12px', borderRadius: '8px',
                        border: `1px solid ${c.line2}`, background: 'transparent',
                        color: c.ink2, fontSize: '12.5px', cursor: 'pointer',
                      }}
                    >
                      Tambah
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {emails.map((email) => (
                      <span
                        key={email}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '3px 8px', borderRadius: '99px',
                          background: c.panel2, color: c.ink2, fontSize: '11.5px',
                        }}
                      >
                        {email}
                        <button
                          type="button"
                          aria-label={`Hapus ${email}`}
                          onClick={() => setEmails(emails.filter((item) => item !== email))}
                          style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            color: c.ink3, padding: 0, lineHeight: 1,
                          }}
                        >×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {link && (
                <div style={{
                  marginTop: '14px', padding: '10px',
                  borderRadius: '10px', border: `1px solid ${c.line}`,
                  background: c.panel,
                }}>
                  <div style={{ fontSize: '11px', color: c.ink3, marginBottom: '6px' }}>Tautan</div>
                  <div style={{
                    fontSize: '12px', color: c.ink2, wordBreak: 'break-all',
                    fontFamily: c.mono, lineHeight: 1.45,
                  }}>
                    {link}
                  </div>
                  {dirty && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: c.ink2 }}>
                      Simpan dulu, baru salin. Perubahan bentuk atau akses belum berlaku.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={copy}
                      disabled={dirty}
                      style={{
                        height: '28px', padding: '0 10px', borderRadius: '99px',
                        border: `1px solid ${dirty ? c.line2 : c.jade}`,
                        background: dirty ? 'transparent' : c.jadeDim,
                        color: dirty ? c.ink3 : c.jade,
                        fontSize: '12px', fontWeight: 600,
                        cursor: dirty ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {copied ? 'Tersalin' : 'Salin tautan'}
                    </button>
                    {confirmRotate ? (
                      <>
                        <button
                          type="button"
                          onClick={rotate}
                          disabled={saving}
                          style={{
                            height: '28px', padding: '0 10px', borderRadius: '99px',
                            border: `1px solid ${c.line2}`, background: 'transparent',
                            color: theme === 'light' ? '#DC2626' : '#F87171',
                            fontSize: '12px', cursor: 'pointer',
                          }}
                        >
                          Tautan lama langsung mati
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmRotate(false)}
                          style={{
                            height: '28px', padding: '0 10px', borderRadius: '99px',
                            border: `1px solid ${c.line2}`, background: 'transparent',
                            color: c.ink3, fontSize: '12px', cursor: 'pointer',
                          }}
                        >
                          Batal
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRotate(true)}
                        style={{
                          height: '28px', padding: '0 10px', borderRadius: '99px',
                          border: `1px solid ${c.line2}`, background: 'transparent',
                          color: c.ink3, fontSize: '12px', cursor: 'pointer',
                        }}
                      >
                        Ganti tautan
                      </button>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: theme === 'light' ? '#DC2626' : '#F87171' }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !canCreate}
                  style={{
                    height: '34px', padding: '0 14px', borderRadius: '99px',
                    border: 'none', background: c.btnPrimaryBg, color: c.btnPrimaryText,
                    fontSize: '13px', fontWeight: 600,
                    cursor: saving || !canCreate ? 'not-allowed' : 'pointer',
                    opacity: canCreate ? 1 : 0.55,
                  }}
                >
                  {share.active ? 'Simpan' : (layout === 'brief' ? 'Buat tautan ringkasan' : 'Buat tautan')}
                </button>
                {share.active && (
                  confirmRevoke ? (
                    <>
                      <button
                        type="button"
                        onClick={revoke}
                        disabled={saving}
                        style={{
                          height: '34px', padding: '0 14px', borderRadius: '99px',
                          border: `1px solid ${c.line2}`, background: 'transparent',
                          color: theme === 'light' ? '#DC2626' : '#F87171',
                          fontSize: '13px', cursor: 'pointer',
                        }}
                      >
                        Penerima tidak bisa buka lagi
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRevoke(false)}
                        style={{
                          height: '34px', padding: '0 14px', borderRadius: '99px',
                          border: `1px solid ${c.line2}`, background: 'transparent',
                          color: c.ink3, fontSize: '13px', cursor: 'pointer',
                        }}
                      >
                        Batal
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmRevoke(true)}
                      style={{
                        height: '34px', padding: '0 14px', borderRadius: '99px',
                        border: `1px solid ${c.line2}`, background: 'transparent',
                        color: c.ink3, fontSize: '13px', cursor: 'pointer',
                      }}
                    >
                      Tutup tautan
                    </button>
                  )
                )}
              </div>
            </>
          )}
        </div>
        {saving && writingBrief && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute', inset: 0, zIndex: 2,
              background: theme === 'dark' ? 'rgba(10,11,13,0.88)' : 'rgba(255,255,255,0.92)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '32px 28px', textAlign: 'center',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: '28px', height: '28px',
                border: `3px solid ${c.line2}`,
                borderTopColor: c.jade,
                borderRadius: '50%',
                animation: 'pa-spin 0.8s linear infinite',
              }}
            />
            <div style={{ marginTop: '16px', fontSize: '15px', fontWeight: 600, color: c.ink }}>
              Menyusun ringkasan
            </div>
            <div style={{ marginTop: '6px', fontSize: '13px', color: c.ink2, lineHeight: 1.45, maxWidth: '280px' }}>
              Ini bisa sampai 30 detik. Jangan tutup jendela ini.
            </div>
            <style>{`@keyframes pa-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareLinkModal;
